import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/layout/Layout";
import { MediaGrid } from "../components/media/MediaGrid";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { FeaturedCarousel } from "../components/media/FeaturedCarousel";
import { Pagination } from "../components/common/Pagination";
import { tmdbApi } from "../services/tmdb";
import { BlogCarousel } from "../components/blog/BlogCarousel";
import blogService from "../services/blogService";
import { PageTitle } from "../components/common/PageTitle";
import { useTranslation } from "../hooks/useTranslation";
import { SEOHead } from "../components/seo/SEOHead";
import { AdBlock } from "../components/ads/AdBlock";
import { NativeBanner } from "../components/ads/NativeBanner";
import { BannerSticky } from "../components/ads/BannerSticky";
import { MobileInterstitialAd } from "../components/ads/MobileInterstitialAd";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import HighPerformanceBanner from "../components/ads/HighPerformanceBanner";
import HilltopAd from '../components/ads/HilltopAd';


const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  
  @media (max-width: 768px) {
    gap: var(--spacing-3);
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  
  &.trending-section {
    margin-top: -2rem;
    
    @media (max-width: 768px) {
      margin-top: -4rem;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
`;

const FEATURED_MOVIES_COUNT = 5;
const MAX_TOTAL_PAGES = 500;

const Home = () => {
  // Scroll automatique vers le haut au chargement de la page
  useScrollToTopOnMount();

  // Hooks d'état
  const [currentMoviePage, setCurrentMoviePage] = useState(1);
  const [currentShowPage, setCurrentShowPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hook de traduction
  const { t } = useTranslation();

  // Requêtes de données
  const {
    data: featuredMovies,
    isLoading: isLoadingFeatured
  } = useQuery({
    queryKey: ["featuredMovies"],
    queryFn: () => tmdbApi.getDiscover("movie", 1, "popularity.desc"),
  });

  const {
    data: trendingMovies,
    isLoading: isLoadingTrending,
    isFetching: isFetchingTrending,
  } = useQuery({
    queryKey: ["trendingMovies", currentMoviePage],
    queryFn: () => tmdbApi.getTrending("movie", "week", currentMoviePage),
  });

  const {
    data: trendingShows,
    isLoading: isLoadingShows,
    error: showsError,
  } = useQuery({
    queryKey: ["trendingShows", currentShowPage],
    queryFn: () => tmdbApi.getTrending("tv", "week", currentShowPage),
  });

  const {
    data: latestPosts,
    isLoading: isLoadingPosts
  } = useQuery({
    queryKey: ["latestBlogPosts"],
    queryFn: () => blogService.getAllPosts(),
  });

  // Gestion du chargement
  if (isLoadingTrending || isLoadingShows || isLoadingFeatured || isLoadingPosts) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  // Gestion des erreurs
  if (showsError) {
    return (
    <Layout>
      <div>{t.common.error}</div>
    </Layout>
  );
  }

  return (
    <Layout>
      <SEOHead
        title={t.home.welcome}
        description={t.home.welcome}
        keywords="cinéma, films, séries, streaming"
      />
      
      <HomeContainer>
        <Section>
          <SectionTitle>À l'affiche</SectionTitle>
          {featuredMovies && (
            <FeaturedCarousel 
              movies={featuredMovies.results.slice(0, FEATURED_MOVIES_COUNT)} 
            />
          )}
          <AdBlock format="banner" />
        </Section>

        {/* Publicité HighPerformance */}
        <HighPerformanceBanner 
          adKey="2154f7f7df439957dfc24240cd37dd3c"
          width={728}
          height={90}
          id="hp-banner-top"
          hideOnMobile
        />
        
        {/* Publicité HilltopAds */}
        <HilltopAd id="hilltop-home-1" />

        <Section className="trending-section">
          <SectionTitle>Films Tendance</SectionTitle>
          {isLoadingTrending ? (
            <LoadingSpinner />
          ) : (
            <>
              <MediaGrid items={trendingMovies?.results || []} type="movie" />
              <Pagination
                currentPage={currentMoviePage}
                totalPages={Math.min(trendingMovies?.total_pages || 1, MAX_TOTAL_PAGES)}
                onPageChange={setCurrentMoviePage}
                isLoading={isFetchingTrending}
              />
            </>
          )}
          <AdBlock format="banner" />
        </Section>

        <Section>
          <SectionTitle>Séries Tendance</SectionTitle>
          {trendingShows && (
            <>
              <MediaGrid items={trendingShows.results} type="tv" />
              <Pagination
                currentPage={currentShowPage}
                totalPages={Math.min(trendingShows.total_pages, MAX_TOTAL_PAGES)}
                onPageChange={setCurrentShowPage}
              />
            </>
          )}
          <AdBlock format="banner" />
        </Section>

        {latestPosts && latestPosts.length > 0 && (
          <Section>
            <SectionTitle>Blog</SectionTitle>
            <BlogCarousel posts={latestPosts} />
          </Section>
        )}
        
        {/* Pub native après la section Blog (si présente) */}
        <NativeBanner />
      </HomeContainer>
      
      {/* Publicité HighPerformance après Blog */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '1rem 0', background: 'var(--background-primary)' }}>
        <HighPerformanceBanner 
          adKey="2154f7f7df439957dfc24240cd37dd3c"
          width={728}
          height={90}
          id="hp-banner-bottom"
        />
      </div>
      {/* Sticky intelligent : droite sur desktop, bas sur mobile */}
      {isMobile ? (
        <BannerSticky position="right" scriptKey="YOUR_SCRIPT_KEY" />
      ) : (
        <BannerSticky position="right" scriptKey="YOUR_SCRIPT_KEY" />
      )}
      {/* Interstitielle mobile (exemple d'appel, à déclencher lors d'un changement de page) */}
      {/* <MobileInterstitialAd show={showInterstitial} onClose={() => setShowInterstitial(false)} /> */}
    </Layout>
  );
};

export default Home;
